import React, { useState, useEffect } from 'react';
import { companyApi, authApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Save, Upload, Building2, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import EinvoiceSettingsCard from '../components/EinvoiceSettingsCard';

const SettingsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Password change state
  const [pwdForm, setPwdForm] = useState({ old: '', new1: '', new2: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdShow, setPwdShow] = useState({ old: false, new1: false, new2: false });
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    cap: '',
    piva: '',
    codice_sdi: '',
    codice_fiscale: '',
    provincia: '',
    email: '',
    phone: '',
    iban: '',
    banca: '',
    logo_base64: null,
    logo_size: 'large',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await companyApi.get();
      setFormData(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle impostazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await companyApi.update(formData);
      toast.success('Impostazioni salvate');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Il logo deve essere inferiore a 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, logo_base64: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setFormData({ ...formData, logo_base64: null });
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    const { old: oldPwd, new1, new2 } = pwdForm;
    if (!oldPwd || !new1 || !new2) {
      toast.error('Compila tutti i campi');
      return;
    }
    if (new1.length < 6) {
      toast.error('La nuova password deve avere almeno 6 caratteri');
      return;
    }
    if (new1 !== new2) {
      toast.error('Le due password non coincidono');
      return;
    }
    if (oldPwd === new1) {
      toast.error('La nuova password deve essere diversa da quella attuale');
      return;
    }
    setPwdSaving(true);
    try {
      await authApi.changeMyPassword(oldPwd, new1);
      toast.success('Password aggiornata');
      setPwdForm({ old: '', new1: '', new2: '' });
      setPwdShow({ old: false, new1: false, new2: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel cambio password');
    } finally {
      setPwdSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight font-['Manrope']">Impostazioni Azienda</h1>
        <p className="text-muted-foreground text-sm">Configura i dati della tua azienda</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Settings */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="card-swiss">
              <CardHeader>
                <CardTitle className="text-base font-semibold font-['Manrope']">Dati Aziendali</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label className="label-uppercase">Ragione Sociale</Label>
                    <Input
                      data-testid="company-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Es. La Tua Azienda S.r.l."
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="label-uppercase">Indirizzo</Label>
                      <Input
                        data-testid="company-address-input"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="Via Roma, 1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-uppercase">Città</Label>
                      <Input
                        data-testid="company-city-input"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        placeholder="Milano"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="label-uppercase">CAP</Label>
                      <Input
                        data-testid="company-cap-input"
                        value={formData.cap}
                        onChange={(e) => setFormData({ ...formData, cap: e.target.value })}
                        placeholder="20100"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-uppercase">Partita IVA</Label>
                      <Input
                        data-testid="company-piva-input"
                        value={formData.piva}
                        onChange={(e) => setFormData({ ...formData, piva: e.target.value })}
                        placeholder="IT00000000000"
                        className="font-['JetBrains_Mono']"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-uppercase">Codice SDI</Label>
                      <Input
                        data-testid="company-sdi-input"
                        value={formData.codice_sdi}
                        onChange={(e) => setFormData({ ...formData, codice_sdi: e.target.value })}
                        placeholder="XXXXXXX"
                        className="font-['JetBrains_Mono']"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="label-uppercase">Codice Fiscale</Label>
                      <Input
                        data-testid="company-cf-input"
                        value={formData.codice_fiscale || ''}
                        onChange={(e) => setFormData({ ...formData, codice_fiscale: e.target.value.toUpperCase() })}
                        placeholder="Se diverso dalla P.IVA"
                        className="font-['JetBrains_Mono'] uppercase"
                        maxLength={16}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-uppercase">Provincia (sigla)</Label>
                      <Input
                        data-testid="company-provincia-input"
                        value={formData.provincia || ''}
                        onChange={(e) => setFormData({ ...formData, provincia: e.target.value.toUpperCase().slice(0, 2) })}
                        placeholder="MI"
                        className="font-['JetBrains_Mono'] uppercase"
                        maxLength={2}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="label-uppercase">Email</Label>
                      <Input
                        data-testid="company-email-input"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="info@azienda.it"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-uppercase">Telefono</Label>
                      <Input
                        data-testid="company-phone-input"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+39 000 0000000"
                      />
                    </div>
                  </div>

                  {/* Coordinate bancarie */}
                  <div className="pt-3 border-t border-border space-y-3">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Coordinate Bancarie (compaiono sul PDF DDT)</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-2">
                        <Label className="label-uppercase">IBAN</Label>
                        <Input
                          data-testid="company-iban-input"
                          value={formData.iban || ''}
                          onChange={(e) => setFormData({ ...formData, iban: e.target.value.toUpperCase().replace(/\s+/g, '') })}
                          placeholder="IT60 X054 2811 1010 0000 0123 456"
                          className="font-['JetBrains_Mono'] uppercase tracking-wider"
                          maxLength={34}
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">
                          27 caratteri per l&apos;Italia. Spazi rimossi automaticamente. Lascia vuoto per non mostrare l&apos;IBAN sul PDF.
                        </p>
                      </div>
                      <div>
                        <Label className="label-uppercase">Banca</Label>
                        <Input
                          data-testid="company-banca-input"
                          value={formData.banca || ''}
                          onChange={(e) => setFormData({ ...formData, banca: e.target.value })}
                          placeholder="Es. Intesa Sanpaolo"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Logo & Actions */}
          <div className="space-y-6">
            <Card className="card-swiss">
              <CardHeader>
                <CardTitle className="text-base font-semibold font-['Manrope']">Logo Aziendale</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {formData.logo_base64 ? (
                    <div className="relative">
                      <img
                        src={formData.logo_base64}
                        alt="Logo aziendale"
                        className="max-w-full h-auto border border-border rounded-sm"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={removeLogo}
                        className="absolute top-2 right-2"
                      >
                        Rimuovi
                      </Button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-border rounded-sm p-8 text-center">
                      <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground mb-4">
                        Nessun logo caricato
                      </p>
                    </div>
                  )}
                  
                  <div>
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                    <label htmlFor="logo-upload">
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full cursor-pointer"
                        asChild
                      >
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          Carica Logo
                        </span>
                      </Button>
                    </label>
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                      PNG, JPG o GIF. Max 2MB.
                    </p>
                  </div>

                  {/* Logo size selector — controlla quanto grande appare nel PDF DDT */}
                  <div className="pt-2 border-t border-border space-y-2" data-testid="logo-size-section">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Dimensione logo nel PDF
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { val: 'small', label: 'Piccolo', hint: '30mm' },
                        { val: 'medium', label: 'Medio', hint: '50mm' },
                        { val: 'large', label: 'Grande', hint: '90mm' },
                      ].map(opt => (
                        <button
                          type="button"
                          key={opt.val}
                          data-testid={`logo-size-${opt.val}`}
                          onClick={() => setFormData({ ...formData, logo_size: opt.val })}
                          className={`px-2 py-2 rounded-sm border text-xs font-medium transition ${
                            formData.logo_size === opt.val
                              ? 'bg-foreground text-background border-foreground'
                              : 'bg-background text-foreground border-border hover:bg-muted'
                          }`}
                        >
                          <div>{opt.label}</div>
                          <div className="text-[9px] opacity-70 font-['JetBrains_Mono']">{opt.hint}</div>
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      Applicato ai DDT stampati dopo il salvataggio.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button
              type="submit"
              data-testid="save-settings-btn"
              disabled={saving}
              className="w-full btn-primary"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvataggio...' : 'SALVA IMPOSTAZIONI'}
            </Button>
          </div>
        </div>
      </form>

      {!user?.is_slave && <EinvoiceSettingsCard />}

      {/* Security: change password (master/slave/super-admin) */}
      <Card data-testid="security-card" className="card-swiss">
        <CardHeader>
          <CardTitle className="text-base font-semibold font-['Manrope'] flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Sicurezza — Cambio password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground mb-4">
            Stai modificando la password dell&apos;account: <span className="font-['JetBrains_Mono'] font-semibold text-foreground">{user?.username || user?.email || '—'}</span>
            {user?.is_slave ? ' (operatore)' : (user?.role === 'company_admin' ? ' (titolare)' : '')}
          </p>
          <form onSubmit={handlePasswordChange} className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Password attuale *</Label>
              <div className="relative">
                <Input
                  data-testid="pwd-old-input"
                  type={pwdShow.old ? 'text' : 'password'}
                  value={pwdForm.old}
                  onChange={(e) => setPwdForm({ ...pwdForm, old: e.target.value })}
                  autoComplete="current-password"
                  className="pr-9"
                  required
                />
                <button
                  type="button"
                  onClick={() => setPwdShow({ ...pwdShow, old: !pwdShow.old })}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {pwdShow.old ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Nuova password *</Label>
              <div className="relative">
                <Input
                  data-testid="pwd-new1-input"
                  type={pwdShow.new1 ? 'text' : 'password'}
                  value={pwdForm.new1}
                  onChange={(e) => setPwdForm({ ...pwdForm, new1: e.target.value })}
                  autoComplete="new-password"
                  placeholder="Min 6 caratteri"
                  className="pr-9"
                  required
                />
                <button
                  type="button"
                  onClick={() => setPwdShow({ ...pwdShow, new1: !pwdShow.new1 })}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {pwdShow.new1 ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Ripeti nuova password *</Label>
              <div className="relative">
                <Input
                  data-testid="pwd-new2-input"
                  type={pwdShow.new2 ? 'text' : 'password'}
                  value={pwdForm.new2}
                  onChange={(e) => setPwdForm({ ...pwdForm, new2: e.target.value })}
                  autoComplete="new-password"
                  placeholder="Reinserisci"
                  className={`pr-9 ${pwdForm.new2 && pwdForm.new1 !== pwdForm.new2 ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setPwdShow({ ...pwdShow, new2: !pwdShow.new2 })}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {pwdShow.new2 ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {pwdForm.new2 && pwdForm.new1 !== pwdForm.new2 && (
                <p className="text-[10px] text-destructive mt-1">Le password non coincidono</p>
              )}
            </div>
            <div className="md:col-span-3 flex justify-end">
              <Button
                type="submit"
                data-testid="pwd-submit-btn"
                disabled={pwdSaving || !pwdForm.old || !pwdForm.new1 || !pwdForm.new2}
                className="btn-primary"
              >
                <Lock className="w-4 h-4 mr-2" />
                {pwdSaving ? 'Aggiornamento...' : 'CAMBIA PASSWORD'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
